import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Phone, Shield, ThumbsUp, Clock, CheckCircle, ArrowRight, Star, Wrench, Home, Building, MapPin } from "lucide-react";
import axios from "axios";
import SEO from "@/components/SEO";
import { LOCATION_KEYWORDS } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOCATIONS = {
  hamilton: {
    name: "Hamilton",
    county: "South Lanarkshire",
    slug: "double-glazing-hamilton",
    nearby: ["Motherwell", "East Kilbride", "Wishaw", "Bellshill", "Blantyre", "Larkhall", "Uddingston"],
    description: "Hamilton is one of the largest towns in South Lanarkshire and home to thousands of properties that benefit from modern double glazing. Whether you live near Hamilton Palace Grounds, in the town centre, or in the surrounding residential areas, our team provides expert double glazing installation tailored to Hamilton homes.",
    localInfo: "As Hamilton's trusted double glazing installers, we understand the unique requirements of properties in this historic South Lanarkshire town. From traditional sandstone homes near Cadzow Castle to modern builds in the newer estates, we supply and fit A-rated uPVC windows and composite doors designed to withstand Scotland's climate while dramatically reducing your heating bills.",
    whyLocal: "Our team has completed hundreds of double glazing installations across Hamilton and the surrounding South Lanarkshire area. We know the local building styles, planning requirements, and what works best for Hamilton homes. From Burnbank to Ferniegair, Low Waters to Whitehill, we've fitted windows and doors throughout the town.",
    areas: ["Burnbank", "Ferniegair", "Low Waters", "Whitehill", "Cadzow", "Hillhouse", "Silvertonhill", "Meikle Earnock", "Quarter", "Bothwell"],
    propertyTypes: "Hamilton features a diverse mix of housing including traditional Victorian and Edwardian sandstone villas, 1930s semi-detached homes, post-war council housing in areas like Hillhouse and Burnbank, and modern private developments. Many older Hamilton properties still have original single-glazed or early double-glazed windows that are now ready for replacement.",
    testimonials: [
      { name: "Margaret T.", area: "Burnbank", text: "Excellent service from start to finish. The new windows have made such a difference to our Hamilton home - much warmer and quieter.", rating: 5 },
      { name: "James M.", area: "Low Waters", text: "Very professional team. They fitted our new composite door in Hamilton and it looks fantastic. Would highly recommend.", rating: 5 },
    ],
  },
  motherwell: {
    name: "Motherwell",
    county: "North Lanarkshire",
    slug: "double-glazing-motherwell",
    nearby: ["Wishaw", "Hamilton", "Bellshill", "Coatbridge", "Airdrie", "Carfin", "Newmains"],
    description: "Motherwell is a vibrant town in North Lanarkshire with a mix of traditional and modern housing. Our double glazing services are designed to improve energy efficiency, security, and kerb appeal for every type of Motherwell property. We are based locally and have been serving Motherwell homeowners for years.",
    localInfo: "As a Motherwell-based double glazing company, we take pride in serving our local community. We know every street and estate in the area, and we understand what Motherwell homeowners need from their windows and doors. Our A-rated double glazing is specifically designed for the Scottish climate, keeping your home warm in winter and cool in summer while cutting your energy bills significantly.",
    whyLocal: "Being based in the Motherwell area means we can offer fast turnaround times, competitive local pricing, and aftercare you can rely on. We've installed double glazing in homes across Motherwell from Forgewood to North Motherwell, Craigneuk to Muirhouse. Our reputation in the area speaks for itself — check our customer reviews.",
    areas: ["Forgewood", "North Motherwell", "Craigneuk", "Muirhouse", "Airbles", "Carfin", "New Stevenston", "Holytown", "Newarthill", "Cleland"],
    propertyTypes: "Motherwell has a wide variety of property types including traditional red sandstone tenements in the town centre, 1950s and 1960s council housing in areas like Forgewood and North Motherwell, semi-detached homes in quieter residential streets, and modern new-build developments. Many Motherwell properties have windows that are 20-30 years old and due for replacement.",
    testimonials: [
      { name: "Sandy W.", area: "Blantyre", text: "We'd shopped around for a while but decided to go with C.Glancey Double Glazing. From start to finish the process was great.", rating: 5 },
      { name: "David M.", area: "Muirhouse", text: "Brilliant service. New windows fitted in our Motherwell home, the difference is incredible. Much warmer and the outside noise is gone.", rating: 5 },
    ],
  },
  "east-kilbride": {
    name: "East Kilbride",
    county: "South Lanarkshire",
    slug: "double-glazing-east-kilbride",
    nearby: ["Hamilton", "Glasgow", "Bothwell", "Blantyre", "Cambuslang", "Newton Mearns"],
    description: "East Kilbride is Scotland's largest new town and home to a wide variety of housing styles, from the original village properties to the extensive post-war estates and modern private developments. Our double glazing solutions are tailored to suit every type of East Kilbride home, improving energy efficiency and comfort all year round.",
    localInfo: "East Kilbride homes were largely built during the new town expansion of the 1950s to 1980s, meaning many properties still have their original single-glazed or early double-glazed windows. Upgrading to modern A-rated double glazing can transform your East Kilbride home — reducing heat loss by up to 50%, lowering energy bills, and dramatically reducing outside noise from busy roads and the town centre.",
    whyLocal: "We have extensive experience working on East Kilbride properties across all the major neighbourhoods. Whether you live in the Village, Westwood, Calderwood, Murray, St Leonards, or any of the surrounding areas, we deliver the same high standard of double glazing installation with competitive pricing and a 10 year guarantee.",
    areas: ["The Village", "Westwood", "Calderwood", "Murray", "St Leonards", "Hairmyres", "Nerston", "Mossneuk", "Stewartfield", "Peel Park"],
    propertyTypes: "East Kilbride is known for its distinctive new town architecture, with the majority of housing built between the 1950s and 1980s. This includes the characteristic Swedish-style timber homes in areas like The Murray, traditional brick semis in Westwood and Calderwood, and high-rise flats in the town centre. Many of these properties still have original windows that can be significantly upgraded.",
    testimonials: [
      { name: "Casey J.", area: "Westwood", text: "I was always kept informed of what was happening and when the windows were being fitted. The fitters were polite and friendly. Would definitely recommend.", rating: 5 },
      { name: "Robert A.", area: "St Leonards", text: "Great job on our East Kilbride home. Professional from quote to completion. Very happy with our new windows.", rating: 5 },
    ],
  },
  airdrie: {
    name: "Airdrie",
    county: "North Lanarkshire",
    slug: "double-glazing-airdrie",
    nearby: ["Coatbridge", "Motherwell", "Cumbernauld", "Bathgate", "Glasgow"],
    description: "Airdrie sits in the heart of North Lanarkshire with excellent transport links to Glasgow and Edinburgh. The town has a fantastic mix of older traditional properties and newer builds, all of which can benefit from high-quality double glazing. Our replacement windows and composite doors are built to last and designed to save you money on heating.",
    localInfo: "Airdrie homeowners face the same challenge as many across central Scotland — older windows that let heat escape and let draughts in. Our A-rated uPVC double glazing is the most cost-effective upgrade you can make to your Airdrie home. We supply and fit a full range of window styles including casement, tilt and turn, bay, and sash windows, all manufactured to the highest standards.",
    whyLocal: "We've been installing double glazing across Airdrie and the wider North Lanarkshire area for years. From Drumgelloch to Clarkston, Whinhall to Gartlea, our team knows the area well and takes pride in delivering quality workmanship to local homeowners. We offer free, no-obligation quotes with no pressure selling.",
    areas: ["Drumgelloch", "Clarkston", "Whinhall", "Gartlea", "Petersburn", "Thrashbush", "Coatdyke", "Calderbank", "Chapelhall", "Plains"],
    propertyTypes: "Airdrie features traditional Scottish tenement flats in the town centre, Victorian and Edwardian terraces, inter-war semi-detached homes, and extensive post-war housing estates. The town has also seen significant new-build development in recent years. Many Airdrie properties have windows that are approaching the end of their lifespan.",
    testimonials: [
      { name: "John D.", area: "Drumgelloch", text: "Fantastic service. New composite door fitted in Airdrie, looks amazing and feels so much more secure.", rating: 5 },
      { name: "Karen S.", area: "Chapelhall", text: "Very impressed with the quality of the windows and the professionalism of the installers. Our Airdrie home is so much warmer now.", rating: 5 },
    ],
  },
  coatbridge: {
    name: "Coatbridge",
    county: "North Lanarkshire",
    slug: "double-glazing-coatbridge",
    nearby: ["Airdrie", "Motherwell", "Glasgow", "Bellshill", "Uddingston"],
    description: "Coatbridge is a town with a rich industrial heritage and a diverse range of residential properties. From terraced homes to semi-detached houses and modern apartments, we provide double glazing solutions for every Coatbridge property. Our energy-efficient windows and doors will transform your home's comfort and appearance.",
    localInfo: "Many Coatbridge homes still have original single-glazed or outdated double-glazed windows that are costing homeowners hundreds in wasted energy every year. Upgrading to our A-rated uPVC double glazing is one of the best investments you can make. Our windows feature multi-chambered profiles, argon gas filled units, and warm edge spacer bars for maximum thermal performance.",
    whyLocal: "Our team has carried out double glazing installations throughout Coatbridge, from the Whifflet area to Old Monkland, Kirkwood to Bargeddie. We understand the local housing stock and can advise on the best window and door solutions for your specific property type. Every installation comes with our 10 year guarantee.",
    areas: ["Whifflet", "Old Monkland", "Kirkwood", "Bargeddie", "Shawhead", "Dundyvan", "Langloan", "Greenend", "Sikeside", "Cliftonhill"],
    propertyTypes: "Coatbridge has a varied housing stock reflecting its industrial past and subsequent regeneration. You'll find traditional miners' cottages, Victorian terraces, inter-war semis, post-war council housing, and modern private developments. Many properties in Coatbridge are ideal candidates for window and door upgrades.",
    testimonials: [
      { name: "Michael H.", area: "Kirkwood", text: "Excellent work on our Coatbridge home. The team were professional, tidy, and the new windows look fantastic.", rating: 5 },
      { name: "Patricia L.", area: "Old Monkland", text: "Very happy with our new double glazing. The difference in warmth is amazing. Great service throughout.", rating: 5 },
    ],
  },
  wishaw: {
    name: "Wishaw",
    county: "North Lanarkshire",
    slug: "double-glazing-wishaw",
    nearby: ["Motherwell", "Hamilton", "Carluke", "Shotts", "Newmains", "Bellshill"],
    description: "Wishaw is a thriving town in North Lanarkshire offering a great community atmosphere and affordable housing. Whether you own a traditional tenement flat, a semi-detached home, or a newer build, our double glazing services will improve your property's energy efficiency, security, and street appeal.",
    localInfo: "Wishaw properties can lose a significant amount of heat through old or inefficient windows. Our replacement double glazing is A-rated for energy efficiency, meaning your home will be warmer, quieter, and cheaper to heat. We offer a complete range of uPVC windows, composite doors, French doors, and patio doors — all available in a wide choice of colours and styles to suit your Wishaw home.",
    whyLocal: "With years of experience serving Wishaw and the surrounding North Lanarkshire towns, we know what local homeowners need. Our installers have worked on properties across Wishaw from Craigneuk to Coltness, Pather to Cambusnethan. We pride ourselves on honest pricing, professional installation, and outstanding aftercare.",
    areas: ["Craigneuk", "Coltness", "Pather", "Cambusnethan", "Overtown", "Waterloo", "Gowkthrapple", "Netherton", "Newmains", "Garrion"],
    propertyTypes: "Wishaw offers a mix of traditional Scottish housing including tenement flats, Victorian terraces, 1930s semis, and extensive post-war council housing in areas like Craigneuk and Coltness. Newer private developments have also been built on the town's outskirts. Many older Wishaw homes have single or early double glazing that's ready for replacement.",
    testimonials: [
      { name: "Susan B.", area: "Coltness", text: "Brilliant job on our windows in Wishaw. The fitters were friendly and efficient. House is so much warmer now.", rating: 5 },
      { name: "Andrew G.", area: "Cambusnethan", text: "Great experience from start to finish. New composite door looks fantastic and very secure. Would recommend.", rating: 5 },
    ],
  },
  bellshill: {
    name: "Bellshill",
    county: "North Lanarkshire",
    slug: "double-glazing-bellshill",
    nearby: ["Motherwell", "Uddingston", "Coatbridge", "Hamilton", "Bothwell", "Viewpark"],
    description: "Bellshill is a well-connected town in North Lanarkshire, ideally situated between Glasgow and the wider Lanarkshire area. The town offers excellent amenities and a range of housing types, all of which can benefit from modern double glazing. Our A-rated uPVC windows and composite doors are designed to improve comfort, security, and energy efficiency.",
    localInfo: "Bellshill homeowners understand the importance of keeping their properties warm and secure. Our double glazing installations are specifically designed for Scottish homes, featuring A-rated sealed units, multi-chambered uPVC profiles, and multi-point locking systems. We've helped hundreds of Bellshill families upgrade their windows and doors over the years.",
    whyLocal: "Our team knows Bellshill well, having completed installations across the town from Mossend to Orbiston, the Main Street area to the surrounding residential estates. We offer competitive pricing, professional installation, and a comprehensive 10 year guarantee. We're not a national company with high overheads — we're local installers who care about our community.",
    areas: ["Mossend", "Orbiston", "Hattonrigg", "Viewpark", "Tannochside", "Birkenshaw", "Uddingston", "Main Street", "Holytown Road", "Calder Road"],
    propertyTypes: "Bellshill has a diverse housing stock including Victorian and Edwardian properties near the town centre, inter-war semis and terraces, post-war council housing in areas like Mossend and Orbiston, and modern private developments. Many Bellshill homes have older windows that no longer provide adequate insulation or security.",
    testimonials: [
      { name: "Linda M.", area: "Mossend", text: "Wonderful service. New windows fitted throughout our Bellshill home. The team were professional and tidy. Highly recommend.", rating: 5 },
      { name: "Steven R.", area: "Viewpark", text: "Excellent quality windows and doors. Our Bellshill home is so much warmer and quieter now. Very happy customers.", rating: 5 },
    ],
  },
  glasgow: {
    name: "Glasgow",
    county: "Glasgow City",
    slug: "double-glazing-glasgow",
    nearby: ["Motherwell", "East Kilbride", "Hamilton", "Coatbridge", "Paisley", "Rutherglen", "Cambuslang"],
    description: "Glasgow is Scotland's largest city with an incredible variety of properties, from stunning Victorian tenements to modern apartments. Our double glazing services cover all areas of Glasgow and are designed to meet the unique requirements of city living. Whether you need replacement windows for a traditional sandstone flat or composite doors for a new build, we can help.",
    localInfo: "Glasgow's diverse architecture presents unique challenges and opportunities for double glazing. Our team has experience working on everything from listed buildings requiring sympathetic restoration to high-rise flats needing practical, durable solutions. We understand local planning requirements and can advise on the best options for your Glasgow property.",
    whyLocal: "Although we're based in Lanarkshire, we regularly work throughout Glasgow and have built an excellent reputation in the city. From the South Side to the West End, Dennistoun to Shawlands, we've installed windows and doors in properties across Glasgow. We offer the same competitive pricing and quality service to all our Glasgow customers.",
    areas: ["South Side", "West End", "Dennistoun", "Shawlands", "Pollokshields", "Maryhill", "Partick", "Govan", "Springburn", "Rutherglen", "Cambuslang", "Baillieston"],
    propertyTypes: "Glasgow is famous for its stunning red and blonde sandstone tenements, which dominate many neighbourhoods. The city also has Victorian villas, inter-war semis, post-war tower blocks, and extensive modern developments. Many Glasgow tenements have original single-glazed sash windows that can be replaced with modern sash-style double glazing for improved efficiency while maintaining the building's character.",
    testimonials: [
      { name: "Emma C.", area: "Shawlands", text: "Fantastic job on our Glasgow tenement flat. New sash-style windows look perfect and have made the flat so much warmer.", rating: 5 },
      { name: "Mark D.", area: "Dennistoun", text: "Excellent service. The team understood exactly what we needed for our Glasgow property. Very professional throughout.", rating: 5 },
    ],
  },
};

const LocationPage = ({ locationKey }) => {
  const loc = LOCATIONS[locationKey];
  const [localProjects, setLocalProjects] = useState([]);
  
  const fetchLocalProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      // Filter projects by location if they match, otherwise show all doors/windows
      const filtered = response.data.filter(p => 
        p.location?.toLowerCase().includes(loc.name.toLowerCase()) ||
        p.location?.toLowerCase().includes(loc.county.toLowerCase().split(' ')[0])
      );
      if (filtered.length > 0) {
        setLocalProjects(filtered.slice(0, 4));
      } else {
        // Show recent projects if no local ones
        setLocalProjects(response.data.slice(0, 4));
      }
    } catch {
      // Silently handle error
    }
  }, [loc.name, loc.county]);

  useEffect(() => {
    if (loc) {
      fetchLocalProjects();
    }
  }, [loc, fetchLocalProjects]);

  if (!loc) return null;

  const services = [
    { title: "A-Rated Double Glazing", desc: `Premium A-rated double glazed windows for ${loc.name} homes. Our energy-efficient windows reduce heat loss by up to 50% and can significantly lower your heating bills.`, icon: Shield },
    { title: "Replacement Windows", desc: `Expert replacement window installation across ${loc.name}. We remove your old windows and fit brand new uPVC double glazing with minimal disruption to your daily routine.`, icon: CheckCircle },
    { title: "Composite Doors", desc: `Secure, stylish composite doors for ${loc.name} properties. Available in a huge range of colours and styles with multi-point locking systems as standard.`, icon: ThumbsUp },
    { title: "Window Repairs", desc: `Fast, reliable window repairs in ${loc.name}. We fix misted units, broken seals, faulty locks, and damaged frames. Often we can repair without full replacement.`, icon: Wrench },
  ];

  const benefits = [
    "Reduce heat loss by up to 50%",
    "Lower your energy bills significantly",
    "A-rated energy efficient glass",
    "Multi-point locking for security",
    "10 year guarantee on all installations",
    "Free, no-obligation quotes",
    "Professional, tidy installers",
    "Wide range of colours and styles",
  ];

  const installationSteps = [
    { step: "01", title: "Free Survey", desc: `We visit your ${loc.name} home to take accurate measurements and discuss your requirements.` },
    { step: "02", title: "Detailed Quote", desc: "You receive a comprehensive written quote with no hidden costs or pressure to commit." },
    { step: "03", title: "Manufacturing", desc: "Your windows are made to exact specifications using premium Rehau or Veka profiles." },
    { step: "04", title: "Installation", desc: `Our experienced team installs your new windows, typically completing most ${loc.name} homes in 1-2 days.` },
  ];

  return (
    <div data-testid={`location-page-${locationKey}`}>
      <SEO
        title={`Double Glazing ${loc.name} | uPVC Windows & Composite Doors ${loc.county}`}
        description={`Double glazing ${loc.name} — A-rated uPVC windows, composite doors, window repairs & replacement windows in ${loc.name}, ${loc.county}. Professional installation, 10 year guarantee. Free quotes — call 07917 181597`}
        keywords={`double glazing ${loc.name}, uPVC windows ${loc.name}, composite doors ${loc.name}, window repairs ${loc.name}, replacement windows ${loc.name}, double glazing installers ${loc.county}, windows and doors ${loc.name}, window installation ${loc.name}, double glazing near me, ${LOCATION_KEYWORDS}`}
        canonical={`/${loc.slug}`}
      />

      {/* Hero */}
      <section className="relative pt-44 pb-20 bg-[#0A0A0A]" data-testid="location-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FF6B00]/10 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-6"
              style={{ fontFamily: "'Chivo', sans-serif" }}
              data-testid="location-h1"
            >
              DOUBLE GLAZING{" "}
              <span className="text-[#FF6B00]">{loc.name.toUpperCase()}</span>
            </h1>
            <p className="text-lg text-gray-300 mb-8">
              {loc.description} We are your local double glazing installers in {loc.name}, offering <Link to="/windows" className="text-[#FF6B00] hover:underline">A-rated uPVC windows</Link>, <Link to="/doors" className="text-[#FF6B00] hover:underline">composite doors</Link>, window repairs, and <Link to="/outbuilds" className="text-[#FF6B00] hover:underline">garden rooms</Link> at competitive prices with a 10 year guarantee.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:07917181597"
                data-testid="location-call-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#FF6B00] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#E65000] transition-all"
              >
                <Phone className="w-5 h-5" />
                Call 07917 181597
              </a>
              <Link
                to="/contact"
                data-testid="location-quote-btn"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-white hover:text-[#0A0A0A] transition-all"
              >
                Get Free Quote <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Double Glazing Installation */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl sm:text-3xl font-black text-[#0A0A0A] mb-6"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            DOUBLE GLAZING INSTALLATION IN <span className="text-[#FF6B00]">{loc.name.toUpperCase()}</span>
          </h2>
          <p className="text-[#525252] text-lg leading-relaxed mb-6">
            {loc.localInfo}
          </p>
          <p className="text-[#525252] leading-relaxed mb-6">
            At C. Glancey Double Glazing, we specialise in supplying and fitting replacement windows and doors across {loc.name} and the wider {loc.county} area. All our double glazing is manufactured using premium Rehau and Veka profiles — the same systems trusted by leading installers throughout the UK. Whether you need a single <Link to="/windows" className="text-[#FF6B00] hover:underline">replacement window</Link> or a complete house of <Link to="/windows" className="text-[#FF6B00] hover:underline">uPVC windows</Link> and <Link to="/doors" className="text-[#FF6B00] hover:underline">doors</Link>, we offer competitive pricing with no hidden costs.
          </p>
          <p className="text-[#525252] leading-relaxed">
            Every double glazing installation we carry out in {loc.name} comes with our 10 year guarantee, giving you complete peace of mind. We also offer window repairs for misty or broken sealed units — in most cases we can replace the glass without changing the frames, saving you time and money. Read what our <Link to="/reviews" className="text-[#FF6B00] hover:underline">customers say about our work</Link>.
          </p>
        </div>
      </section>

      {/* Services: uPVC Windows, Composite Doors, Repairs */}
      <section className="py-16 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl sm:text-3xl font-black text-[#0A0A0A] text-center mb-4"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            UPVC WINDOWS & COMPOSITE DOORS IN <span className="text-[#FF6B00]">{loc.name.toUpperCase()}</span>
          </h2>
          <p className="text-center text-[#525252] max-w-2xl mx-auto mb-12">
            We provide a complete range of double glazing services for {loc.name} homeowners, including uPVC windows, composite doors, window repairs, and garden rooms.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service) => (
              <div key={service.title} className="bg-white p-8 border-l-4 border-[#FF6B00]">
                <service.icon className="w-8 h-8 text-[#FF6B00] mb-4" />
                <h3 className="text-lg font-bold text-[#0A0A0A] mb-3">{service.title}</h3>
                <p className="text-[#525252]">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Types of Properties */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl sm:text-3xl font-black text-[#0A0A0A] mb-6"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            TYPES OF PROPERTIES IN <span className="text-[#FF6B00]">{loc.name.toUpperCase()}</span>
          </h2>
          <div className="flex items-start gap-4 mb-6">
            <Building className="w-12 h-12 text-[#FF6B00] flex-shrink-0" />
            <p className="text-[#525252] leading-relaxed">
              {loc.propertyTypes}
            </p>
          </div>
          <p className="text-[#525252] leading-relaxed">
            Whatever type of property you own in {loc.name}, our experienced team can advise on the best double glazing solutions. We work on everything from traditional tenement flats to modern detached homes, always tailoring our approach to suit the specific requirements of your property. Our windows are available in white, anthracite grey, black, woodgrain effects, and many other colours to complement your home's character.
          </p>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl sm:text-3xl font-black text-white text-center mb-4"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            OUR INSTALLATION PROCESS IN <span className="text-[#FF6B00]">{loc.name.toUpperCase()}</span>
          </h2>
          <p className="text-gray-300 text-center max-w-2xl mx-auto mb-12">
            From your initial enquiry to the final installation, we make getting new double glazing in {loc.name} as simple as possible.
          </p>
          <div className="grid md:grid-cols-4 gap-8">
            {installationSteps.map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 bg-[#FF6B00] text-white font-black text-2xl flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Local Projects */}
      <section className="py-16 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <h2
              className="text-2xl sm:text-3xl font-black text-[#0A0A0A]"
              style={{ fontFamily: "'Chivo', sans-serif" }}
            >
              RECENT {loc.name.toUpperCase()} <span className="text-[#FF6B00]">PROJECTS</span>
            </h2>
            <Link to="/recent-work" className="text-[#FF6B00] font-bold uppercase text-sm hover:underline flex items-center gap-2">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {localProjects.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {localProjects.map((project) => (
                <div key={project.id} className="bg-white border border-[#E5E5E5] overflow-hidden group hover:border-[#FF6B00] transition-all">
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={project.image_url}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-[#0A0A0A] text-sm mb-1">{project.title}</h3>
                    <p className="text-xs text-[#525252]">{project.location || loc.name}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-[#525252]">Loading recent projects...</p>
          )}
        </div>
      </section>

      {/* Customer Testimonials from Location */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl sm:text-3xl font-black text-[#0A0A0A] mb-8"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            CUSTOMER TESTIMONIALS FROM <span className="text-[#FF6B00]">{loc.name.toUpperCase()}</span>
          </h2>
          <div className="space-y-6">
            {loc.testimonials.map((testimonial, idx) => (
              <div key={`testimonial-${testimonial.name}-${idx}`} className="bg-[#F5F5F5] p-6 border-l-4 border-[#FF6B00]">
                <div className="flex mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-5 h-5 ${star <= testimonial.rating ? "text-[#FF6B00] fill-[#FF6B00]" : "text-gray-300"}`}
                    />
                  ))}
                </div>
                <p className="text-[#525252] italic mb-4">"{testimonial.text}"</p>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-[#0A0A0A]">{testimonial.name}</span>
                  <span className="text-[#525252]">—</span>
                  <span className="text-[#525252]">{testimonial.area}, {loc.name}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/reviews" className="text-[#FF6B00] font-bold uppercase text-sm hover:underline">
              Read More Customer Reviews →
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl sm:text-3xl font-black text-white text-center mb-4"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            WHY CHOOSE US FOR DOUBLE GLAZING IN <span className="text-[#FF6B00]">{loc.name.toUpperCase()}</span>?
          </h2>
          <p className="text-gray-300 text-center max-w-2xl mx-auto mb-12">
            {loc.whyLocal}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit) => (
              <div key={benefit} className="flex items-start gap-3 bg-[#1A1A1A] p-5">
                <CheckCircle className="w-5 h-5 text-[#FF6B00] flex-shrink-0 mt-0.5" />
                <span className="text-gray-300 text-sm">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas/Neighbourhoods Covered */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl sm:text-3xl font-black text-[#0A0A0A] mb-6"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            AREAS & NEIGHBOURHOODS IN <span className="text-[#FF6B00]">{loc.name.toUpperCase()}</span>
          </h2>
          <p className="text-[#525252] mb-8">
            We install double glazing, replacement windows, composite doors, and provide window repairs throughout {loc.name} and the surrounding {loc.county} areas. Our double glazing installers cover the following neighbourhoods in {loc.name}:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-8">
            {loc.areas.map((area) => (
              <div key={area} className="bg-[#F5F5F5] px-4 py-3 text-center text-sm font-medium text-[#0A0A0A] flex items-center justify-center gap-2">
                <MapPin className="w-3 h-3 text-[#FF6B00]" />
                {area}
              </div>
            ))}
          </div>
          <p className="text-[#525252]">
            We also serve nearby towns including{" "}
            {loc.nearby.map((town, i) => {
              const hasPage = Object.values(LOCATIONS).find(l => l.name === town);
              if (hasPage) {
                return (
                  <span key={town}>
                    <Link to={`/${hasPage.slug}`} className="text-[#FF6B00] hover:underline">{town}</Link>
                    {i < loc.nearby.length - 1 ? ", " : ""}
                  </span>
                );
              }
              return <span key={town}>{town}{i < loc.nearby.length - 1 ? ", " : ""}</span>;
            })}
            {" "}and across {loc.county}. <Link to="/contact" className="text-[#FF6B00] hover:underline">Contact us</Link> for a free quote in your area.
          </p>
        </div>
      </section>

      {/* Location-Specific FAQ */}
      <section className="py-16 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl sm:text-3xl font-black text-[#0A0A0A] mb-8"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            DOUBLE GLAZING {loc.name.toUpperCase()} <span className="text-[#FF6B00]">FAQ</span>
          </h2>
          <div className="space-y-6">
            <div className="bg-white p-6">
              <h3 className="font-bold text-[#0A0A0A] mb-2">How much does double glazing cost in {loc.name}?</h3>
              <p className="text-[#525252]">The cost of double glazing in {loc.name} depends on the number and size of windows, the style chosen, and your property type. We offer free, no-obligation quotes so you can get an accurate price for your {loc.name} home. Our prices are competitive and we never use pressure selling tactics.</p>
            </div>
            <div className="bg-white p-6">
              <h3 className="font-bold text-[#0A0A0A] mb-2">Do you install uPVC windows in {loc.name}?</h3>
              <p className="text-[#525252]">Yes, we specialise in uPVC window installation throughout {loc.name} and {loc.county}. Our uPVC windows are A-rated for energy efficiency and available in casement, tilt and turn, bay, and sash styles. We offer a wide range of colours including white, anthracite grey, black, and woodgrain finishes.</p>
            </div>
            <div className="bg-white p-6">
              <h3 className="font-bold text-[#0A0A0A] mb-2">Can you fit composite doors in {loc.name}?</h3>
              <p className="text-[#525252]">Absolutely. We supply and fit a wide range of composite doors for {loc.name} homes. Composite doors are incredibly secure, energy-efficient, and low-maintenance. They're available in many colours and styles with multi-point locking systems as standard.</p>
            </div>
            <div className="bg-white p-6">
              <h3 className="font-bold text-[#0A0A0A] mb-2">Do you offer window repairs in {loc.name}?</h3>
              <p className="text-[#525252]">Yes, we provide window repairs across {loc.name} including misted unit replacement, broken seal repairs, faulty lock repairs, and damaged frame repairs. In many cases, we can repair your existing windows without full replacement, saving you money.</p>
            </div>
            <div className="bg-white p-6">
              <h3 className="font-bold text-[#0A0A0A] mb-2">How long does window installation take in {loc.name}?</h3>
              <p className="text-[#525252]">Most double glazing installations in {loc.name} are completed within one to two days depending on the size of the job. Our experienced installers work efficiently and tidily to minimise disruption. We always leave your {loc.name} home clean and tidy after every installation.</p>
            </div>
            <div className="bg-white p-6">
              <h3 className="font-bold text-[#0A0A0A] mb-2">What guarantee do you offer on double glazing in {loc.name}?</h3>
              <p className="text-[#525252]">All our double glazing installations in {loc.name} come with a comprehensive 10 year guarantee covering both the products and the workmanship. We use only premium Rehau and Veka profiles with A-rated sealed units, ensuring your new windows and doors will last for decades.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#FF6B00]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-2xl sm:text-3xl font-black text-white mb-4"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            GET A FREE DOUBLE GLAZING QUOTE IN {loc.name.toUpperCase()}
          </h2>
          <p className="text-white/90 mb-8">
            Ready to upgrade your {loc.name} home with new double glazing, <Link to="/windows" className="text-white underline hover:text-[#0A0A0A]">uPVC windows</Link>, or <Link to="/doors" className="text-white underline hover:text-[#0A0A0A]">composite doors</Link>? Call us today or request a free quote online. We also offer window repairs and build bespoke <Link to="/outbuilds" className="text-white underline hover:text-[#0A0A0A]">garden rooms</Link>.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:07917181597"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#0A0A0A] font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#0A0A0A] hover:text-white transition-all"
            >
              <Phone className="w-5 h-5" />
              Call 07917 181597
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-white hover:text-[#0A0A0A] transition-all"
            >
              Request Free Quote <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Related Pages */}
      <section className="py-12 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-bold text-[#0A0A0A] mb-6">Double Glazing Near {loc.name}</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {Object.values(LOCATIONS).filter(l => l.slug !== loc.slug).map(l => (
              <Link key={l.slug} to={`/${l.slug}`} className="px-5 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
                Double Glazing {l.name}
              </Link>
            ))}
            <Link to="/windows" className="px-5 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              uPVC Windows
            </Link>
            <Link to="/doors" className="px-5 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Composite Doors
            </Link>
            <Link to="/outbuilds" className="px-5 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Garden Rooms
            </Link>
            <Link to="/reviews" className="px-5 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Customer Reviews
            </Link>
            <Link to="/recent-work" className="px-5 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Recent Work
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export { LOCATIONS };
export default LocationPage;
